import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { Button, Icon, IconButton } from '@shopflo/ui'
import { CaretLeft, CaretRight, Wrench } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import SaveDiscountDialog from '../dialogs/SaveDiscountDialog'
import { useDiscountForm } from '../hooks/useDiscountForm'
import useGTM from 'src/lib/hooks/useGTM'

const DiscountV2Layout = ({
  children,
  ignoreLayout,
  hideLegacyBuilder,
  disableSave
}: {
  children: React.ReactNode
  ignoreLayout?: boolean
  hideLegacyBuilder?: boolean
  disableSave?: boolean
}) => {
  const router = useRouter()
  const { t } = useLocale()
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const {
    actions: { validateForm }
  } = useDiscountForm()
  const { sendGTMTrigger } = useGTM()
  const handleSaveClick = () => {
    if (validateForm()) {
      setIsSaveDialogOpen(true)
    }
  }

  if (ignoreLayout) {
    return <>{children}</>
  }

  return (
    <div className='flex flex-col h-screen'>
      <header className='flex items-center justify-between px-12 pt-5 pb-2 z-20 bg-zinc-50'>
        <div className='flex items-center gap-2'>
          <IconButton
            variant='outline'
            IconComponent={CaretLeft}
            onClick={() => router.back()}
            size='sm'
            className='text-zinc-500'
            iconProps={{ weight: 'bold', className: 'w-3 h-3' }}
          />
          <div className='flex items-center text-text-sm-medium '>
            <span
              className='text-text-sm-semibold cursor-pointer text-zinc-500 hover:text-zinc-700 transition-colors'
              onClick={() => router.push('/discounts')}
            >
              {t('discounts_v2.discounts')}
            </span>
            <Icon
              IconComponent={CaretRight}
              size='md'
              weight='bold'
              className='text-zinc-600'
              iconProps={{ weight: 'bold', className: 'w-3 h-3' }}
            />
            <span className='text-text-sm-semibold text-zinc-600'>
              {router.query.discountId ? t('discounts_v2.edit_discount') : t('discounts_v2.new_discount')}
            </span>
          </div>
        </div>

        <div className='flex items-center gap-1.5'>
          {!hideLegacyBuilder && (
            <>
              <Button
                variant='ghost'
                size='sm'
                leftIcon={Wrench}
                className='text-zinc-500'
                onClick={() => {
                  const currentPath = router.asPath
                  const legacyPath = currentPath.replace('/v2/', '/')
                  sendGTMTrigger('legacy_discount_builder_clicked', 'click')
                  router.push(legacyPath)
                }}
              >
                {t('discounts_v2.legacy_builder')}
              </Button>
              <div className='w-2 justify-center flex'>
                <div className='h-6 w-[1px] bg-zinc-200'></div>
              </div>
            </>
          )}
          <div className='flex items-center gap-1'>
            <Button variant='outline' size='sm' onClick={() => router.back()}>
              {t('cancel')}
            </Button>
            <Button
              variant='primary'
              size='sm'
              onClick={handleSaveClick}
              disabled={disableSave}
              data-track-id='discount-save-button'
            >
              {t('save')}
            </Button>
          </div>
        </div>
      </header>

      <div className='flex-1 overflow-y-auto'>{children}</div>

      <SaveDiscountDialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen} />
    </div>
  )
}

export default DiscountV2Layout
